import React, { useEffect, useMemo, useState } from "react";

import {
  buildRuntimeSeoMetadata,
  resolveSeoKeyFromHash,
  serializeJsonLdForHtml
} from "../app/seo";
import type { Locale } from "../content/types";
import {
  DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS,
  buildRatingsSummaryQuery,
  emptyPublicRatingsSummary,
  getDynamicBooksPublicRatingsBootstrap,
  getDynamicBooksRatingsTarget,
  isPublicRatingsSummarySchemaEligible,
  normalizePublicRatingsSummary,
  publishDynamicBooksPublicRatingsSummary,
  type PublicRatingsEndpoints,
  type PublicRatingsSummary
} from "../lib/publicRatings";

type RatingsUiState = "empty" | "challenge_sent" | "verified" | "pending_moderation" | "approved_aggregate" | "error";

type PublicApiEnvelope<T> =
  | {
      ok: true;
      data: T;
    }
  | {
      ok: false;
      error?: {
        message?: string;
      };
    };

type ChallengeResponse = {
  challenge_id?: string;
  masked_email?: string;
};

type VerifyResponse = {
  rating_submission_token?: string;
};

const ratingValues = [1, 2, 3, 4, 5] as const;

const copy = {
  sv: {
    eyebrow: "Verifierade betyg",
    title: "Betygsätt DynamicBooks programvara",
    body: "Betyg tas emot efter e-postverifiering och blir publika först efter moderering.",
    emptyTitle: "Inget godkänt publikt betyg än",
    emptyBody: "Dela ett verifierat betyg för DynamicBooks programvara.",
    aggregate: (summary: PublicRatingsSummary) =>
      `${formatRating(summary)} av 5 från ${summary.rating_count} verifierade betyg`,
    selectStart: "Välj en stjärna för att börja.",
    selected: (rating: number) => `${rating} av 5 valt. Ange din e-post för verifiering.`,
    chooseRating: "Välj ett betyg",
    emailLabel: "E-post för verifiering",
    sendCode: "Skicka kod",
    sending: "Skickar...",
    codeSent: (maskedEmail: string) => `Verifieringskod skickad${maskedEmail ? ` till ${maskedEmail}` : ""}.`,
    codeLabel: (maskedEmail: string) => `Verifieringskod${maskedEmail ? ` för ${maskedEmail}` : ""}`,
    verify: "Verifiera",
    verifying: "Verifierar...",
    displayName: "Visningsnamn",
    comment: "Kommentar",
    submit: "Skicka betyg",
    submitting: "Skickar...",
    verified: "E-post verifierad. Lägg till en frivillig kommentar och skicka betyget.",
    pending: "Betyget har skickats och väntar på moderering.",
    errors: {
      rating: "Välj ett stjärnbetyg först.",
      email: "Ange en giltig e-postadress.",
      code: "Ange verifieringskoden.",
      submit: "Verifiera din e-post innan du skickar ett betyg.",
      challenge: "Verifieringskoden kunde inte skickas.",
      verify: "Verifieringskoden kunde inte verifieras.",
      submission: "Betyget kunde inte skickas.",
      generic: "Betygsförfrågan kunde inte slutföras."
    }
  },
  en: {
    eyebrow: "Verified ratings",
    title: "Rate DynamicBooks software",
    body: "Ratings are accepted after email verification and become public only after moderation.",
    emptyTitle: "No approved public rating yet",
    emptyBody: "Share a verified DynamicBooks software rating.",
    aggregate: (summary: PublicRatingsSummary) =>
      `${formatRating(summary)} out of 5 from ${summary.rating_count} verified ratings`,
    selectStart: "Select a star to begin.",
    selected: (rating: number) => `${rating} out of 5 selected. Enter your email to verify.`,
    chooseRating: "Choose a rating",
    emailLabel: "Verification address",
    sendCode: "Send code",
    sending: "Sending...",
    codeSent: (maskedEmail: string) => `Verification code sent${maskedEmail ? ` to ${maskedEmail}` : ""}.`,
    codeLabel: (maskedEmail: string) => `Verification code${maskedEmail ? ` for ${maskedEmail}` : ""}`,
    verify: "Verify",
    verifying: "Verifying...",
    displayName: "Display name",
    comment: "Comment",
    submit: "Submit rating",
    submitting: "Submitting...",
    verified: "Email verified. Add an optional comment and submit your rating.",
    pending: "Rating submitted and pending moderation.",
    errors: {
      rating: "Select a star rating first.",
      email: "Enter a valid email address.",
      code: "Enter the verification code.",
      submit: "Verify your email before submitting a rating.",
      challenge: "The verification code could not be sent.",
      verify: "The verification code could not be verified.",
      submission: "The rating could not be submitted.",
      generic: "The rating request could not be completed."
    }
  }
} satisfies Record<Locale, Record<string, unknown>>;

type SoftwareRatingsWidgetProps = {
  locale: Locale;
};

export function SoftwareRatingsWidget({ locale }: SoftwareRatingsWidgetProps) {
  const text = copy[locale];
  const target = useMemo(() => getDynamicBooksRatingsTarget(locale), [locale]);
  const bootstrap = useMemo(() => getDynamicBooksPublicRatingsBootstrap(target), [target]);
  const endpoints = useMemo(() => bootstrap?.endpoints ?? DYNAMICBOOKS_PUBLIC_RATINGS_ENDPOINTS, [bootstrap]);
  const initialSummary = useMemo(
    () => normalizePublicRatingsSummary(bootstrap?.summary ?? emptyPublicRatingsSummary(target), target),
    [bootstrap, target]
  );
  const [summary, setSummary] = useState<PublicRatingsSummary>(() => initialSummary);
  const [uiState, setUiState] = useState<RatingsUiState>(() =>
    isPublicRatingsSummarySchemaEligible(initialSummary, target) ? "approved_aggregate" : "empty"
  );
  const [statusText, setStatusText] = useState(() =>
    isPublicRatingsSummarySchemaEligible(initialSummary, target) ? text.aggregate(initialSummary) : text.selectStart
  );
  const [rating, setRating] = useState(0);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [comment, setComment] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [submissionToken, setSubmissionToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [busy, setBusy] = useState<"summary" | "challenge" | "verify" | "submit" | null>(null);

  useEffect(() => {
    let cancelled = false;

    void refreshSummary(endpoints, target).then((nextSummary) => {
      if (!cancelled && nextSummary) {
        applySummary(nextSummary, { keepState: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [endpoints, target]);

  const aggregateVisible = isPublicRatingsSummarySchemaEligible(summary, target);

  function applySummary(nextSummary: PublicRatingsSummary, options: { keepState?: boolean } = {}) {
    const normalized = normalizePublicRatingsSummary(nextSummary, target);

    setSummary(normalized);
    publishDynamicBooksPublicRatingsSummary(normalized, target);
    syncDynamicBooksJsonLd(locale, normalized);

    if (isPublicRatingsSummarySchemaEligible(normalized, target)) {
      const message = text.aggregate(normalized);

      if (!options.keepState || uiState === "empty" || uiState === "approved_aggregate") {
        setUiState("approved_aggregate");
        setStatusText(message);
      }
    } else if (!options.keepState && uiState === "approved_aggregate") {
      setUiState("empty");
      setStatusText(text.selectStart);
    }
  }

  function selectRating(nextRating: number) {
    setRating(nextRating);
    setUiState("empty");
    setStatusText(text.selected(nextRating));
  }

  async function startChallenge() {
    if (!rating) {
      setUiState("error");
      setStatusText(text.errors.rating);
      return;
    }

    if (!isLikelyEmail(email)) {
      setUiState("error");
      setStatusText(text.errors.email);
      return;
    }

    setBusy("challenge");

    try {
      const data = await postPublicJson<ChallengeResponse>(endpoints.challenges, {
        host: target.host,
        product_key: target.product_key,
        locale: target.locale,
        email
      });

      setChallengeId(String(data.challenge_id ?? ""));
      setSubmissionToken("");
      setMaskedEmail(String(data.masked_email ?? ""));
      setEmail("");
      setUiState("challenge_sent");
      setStatusText(text.codeSent(String(data.masked_email ?? "")));
    } catch (error) {
      setUiState("error");
      setStatusText(error instanceof Error ? error.message : text.errors.challenge);
    } finally {
      setBusy(null);
    }
  }

  async function verifyCode() {
    if (!challengeId || !code.trim()) {
      setUiState("error");
      setStatusText(text.errors.code);
      return;
    }

    setBusy("verify");

    try {
      const data = await postPublicJson<VerifyResponse>(
        `${endpoints.challenges}/${encodeURIComponent(challengeId)}/verify`,
        {
          code: code.trim()
        }
      );

      setSubmissionToken(String(data.rating_submission_token ?? ""));
      setCode("");
      setUiState("verified");
      setStatusText(text.verified);
    } catch (error) {
      setUiState("error");
      setStatusText(error instanceof Error ? error.message : text.errors.verify);
    } finally {
      setBusy(null);
    }
  }

  async function submitRating() {
    if (!rating || !submissionToken) {
      setUiState("error");
      setStatusText(text.errors.submit);
      return;
    }

    setBusy("submit");

    try {
      await postPublicJson(endpoints.submissions, {
        host: target.host,
        product_key: target.product_key,
        locale: target.locale,
        rating,
        comment: comment.trim(),
        reviewer_display_name: displayName.trim(),
        rating_submission_token: submissionToken
      });
      setSubmissionToken("");
      setComment("");
      setDisplayName("");
      setChallengeId("");
      setUiState("pending_moderation");
      setStatusText(text.pending);
      const nextSummary = await refreshSummary(endpoints, target);

      if (nextSummary) {
        applySummary(nextSummary, { keepState: true });
      }
    } catch (error) {
      setUiState("error");
      setStatusText(error instanceof Error ? error.message : text.errors.submission);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="software-ratings-widget" aria-labelledby="software-ratings-title" data-dynamicbooks-software-ratings>
      <div className="software-ratings-widget__heading">
        <p className="section-eyebrow">{text.eyebrow}</p>
        <h3 id="software-ratings-title">{text.title}</h3>
        <p>{text.body}</p>
      </div>

      {aggregateVisible ? (
        <div className="software-ratings-widget__aggregate" data-dynamicbooks-ratings-aggregate>
          <div className="software-ratings-widget__aggregate-stars" aria-label={text.aggregate(summary)}>
            {"★★★★★"}
          </div>
          <p>
            <strong data-dynamicbooks-ratings-value>{formatRating(summary)}</strong>
            <span>{locale === "sv" ? " av 5 från " : " out of 5 from "}</span>
            <span data-dynamicbooks-ratings-count>{summary.rating_count}</span>
            <span>{locale === "sv" ? " verifierade betyg" : " verified ratings"}</span>
          </p>
        </div>
      ) : (
        <div className="software-ratings-widget__empty" data-dynamicbooks-ratings-empty>
          <strong>{text.emptyTitle}</strong>
          <span>{text.emptyBody}</span>
        </div>
      )}

      <form className="software-ratings-widget__form" onSubmit={(event) => event.preventDefault()} noValidate>
        <fieldset className="software-ratings-widget__stars" aria-label={text.chooseRating}>
          {ratingValues.map((value) => (
            <button
              className={`software-ratings-widget__star${rating >= value ? " software-ratings-widget__star--active" : ""}`}
              key={value}
              type="button"
              aria-pressed={rating === value}
              data-rating-value={value}
              disabled={busy !== null}
              onClick={() => selectRating(value)}
            >
              ★
            </button>
          ))}
        </fieldset>

        {uiState !== "verified" && uiState !== "pending_moderation" ? (
          <div className="software-ratings-widget__fields" data-dynamicbooks-ratings-email-panel>
            <label htmlFor={`software-rating-email-${locale}`}>{text.emailLabel}</label>
            <input
              id={`software-rating-email-${locale}`}
              type="email"
              autoComplete="email"
              value={email}
              disabled={busy !== null}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
            <button className="db-button db-button--primary" type="button" onClick={startChallenge} disabled={busy !== null}>
              {busy === "challenge" ? text.sending : text.sendCode}
            </button>
          </div>
        ) : null}

        {uiState === "challenge_sent" || Boolean(challengeId && !submissionToken && uiState === "error") ? (
          <div className="software-ratings-widget__fields" data-dynamicbooks-ratings-code-panel>
            <label htmlFor={`software-rating-code-${locale}`}>{text.codeLabel(maskedEmail)}</label>
            <input
              id={`software-rating-code-${locale}`}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              disabled={busy !== null}
              onChange={(event) => setCode(event.currentTarget.value)}
            />
            <button className="db-button db-button--primary" type="button" onClick={verifyCode} disabled={busy !== null}>
              {busy === "verify" ? text.verifying : text.verify}
            </button>
          </div>
        ) : null}

        {uiState === "verified" ? (
          <div className="software-ratings-widget__fields software-ratings-widget__fields--submit" data-dynamicbooks-ratings-submit-panel>
            <label htmlFor={`software-rating-display-name-${locale}`}>{text.displayName}</label>
            <input
              id={`software-rating-display-name-${locale}`}
              type="text"
              value={displayName}
              disabled={busy !== null}
              onChange={(event) => setDisplayName(event.currentTarget.value)}
            />
            <label htmlFor={`software-rating-comment-${locale}`}>{text.comment}</label>
            <textarea
              id={`software-rating-comment-${locale}`}
              value={comment}
              disabled={busy !== null}
              rows={3}
              onChange={(event) => setComment(event.currentTarget.value)}
            />
            <button className="db-button db-button--primary" type="button" onClick={submitRating} disabled={busy !== null}>
              {busy === "submit" ? text.submitting : text.submit}
            </button>
          </div>
        ) : null}
      </form>

      <p
        className={`software-ratings-widget__status${uiState === "error" ? " software-ratings-widget__status--error" : ""}`}
        role="status"
        data-dynamicbooks-ratings-status
      >
        {statusText}
      </p>
    </section>
  );
}

async function refreshSummary(
  endpoints: PublicRatingsEndpoints,
  target: ReturnType<typeof getDynamicBooksRatingsTarget>
): Promise<PublicRatingsSummary | null> {
  try {
    const response = await fetch(buildRatingsSummaryQuery(target, endpoints.summary), {
      method: "GET",
      credentials: "same-origin"
    });
    const payload = (await response.json()) as PublicApiEnvelope<PublicRatingsSummary>;

    if (!response.ok || !payload.ok) {
      return null;
    }

    return normalizePublicRatingsSummary(payload.data, target);
  } catch {
    return null;
  }
}

async function postPublicJson<T = unknown>(url: string, payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const envelope = (await response.json()) as PublicApiEnvelope<T>;

  if (!response.ok || !envelope.ok) {
    throw new Error(envelope.ok ? "The rating request could not be completed." : envelope.error?.message ?? "The rating request could not be completed.");
  }

  return envelope.data;
}

function syncDynamicBooksJsonLd(locale: Locale, summary: PublicRatingsSummary): void {
  if (typeof document === "undefined") {
    return;
  }

  const script = document.querySelector<HTMLScriptElement>("#dynamicbooks-jsonld");

  if (!script) {
    return;
  }

  const key = resolveSeoKeyFromHash(window.location.hash);
  script.textContent = serializeJsonLdForHtml(buildRuntimeSeoMetadata(locale, key, {
    publicRatingsSummary: summary
  }).jsonLd);
}

function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function formatRating(summary: PublicRatingsSummary): string {
  return summary.rating_value === null ? "" : summary.rating_value.toFixed(1);
}
